import React, { useState } from "react";
import { format } from "date-fns";
import { Tooltip, IconButton } from "@material-ui/core";
import { ExpandMore, ExpandLess } from "@material-ui/icons";
import { Text } from "../../../../../components";
import { PreOrderStatusText } from "../../../../../constants/PreOrderStatus";
import {
  StyledStatusContainer,
  StyledStatusText,
  ProductsContainer,
  ProductsHeader,
  ProductsTitle,
  ProductItem,
  ProductName,
  ProductQuantity,
  StyledRowWrapper,
} from "./styles";
import { USER_ROLES } from "../../../../../constants";

export const columnType = {
  CREATED_AT: "createdAt",
  PRE_ORDER_NAME: "preOrderName",
  PRE_ORDER_NUMBER: "preOrderNumber",
  AUTHOR: "authorUser",
  STATUS: "status",
  EXPAND: "expand",
};

export const columnLabel = {
  [columnType.CREATED_AT]: "Data de Criação",
  [columnType.PRE_ORDER_NAME]: "Nome do Pré-pedido",
  [columnType.PRE_ORDER_NUMBER]: "Número do Pré-pedido",
  [columnType.AUTHOR]: "Autor",
  [columnType.STATUS]: "Status",
  [columnType.EXPAND]: "",
};

const translateColumnStatus = (status) =>
  PreOrderStatusText[status] ?? "--";

export const usePreOrdersTable = ({ data, userData }) => {
  const isAgent = userData.userRole === USER_ROLES.AGENT;
  const isEmployee = userData.userRole === USER_ROLES.EMPLOYEE;
  const [expandedRows, setExpandedRows] = useState(new Set());

  const hasProducts = (rowData) =>
    Array.isArray(rowData?.products) && rowData.products.length > 0;

  const toggleRowExpansion = (rowIndex) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(rowIndex)) {
      newExpandedRows.delete(rowIndex);
    } else {
      newExpandedRows.add(rowIndex);
    }
    setExpandedRows(newExpandedRows);
  };

  const renderProductsList = (rowData) => {
    const products = rowData?.products;
    if (!products?.length) return null;

    return (
      <ProductsContainer>
        <ProductsHeader>
          <ProductsTitle>Produto</ProductsTitle>
          <ProductsTitle quantity>Quantidade de materiais</ProductsTitle>
        </ProductsHeader>
        {products.map((product, index) => (
          <ProductItem key={index}>
            <ProductName>{product.materialName || "--"}</ProductName>
            <ProductQuantity>{product.quantity}</ProductQuantity>
          </ProductItem>
        ))}
      </ProductsContainer>
    );
  };

  const _renderBasicTextCell = (value) => (
    <Tooltip title={value || ""} arrow placement="top">
      <div>
        <Text
          numberOfLines={2}
          style={{ fontSize: 14, maxWidth: 100, wordWrap: "break-word" }}
          key={`key-${value}`}
        >
          {value || "--"}
        </Text>
      </div>
    </Tooltip>
  );

  const _renderStatusTextCell = (translatedValue, originalValue) => (
    <Tooltip title={translatedValue || ""} arrow placement="top">
      <StyledStatusContainer status={originalValue}>
        <StyledStatusText status={originalValue}>
          {translatedValue || "--"}
        </StyledStatusText>
      </StyledStatusContainer>
    </Tooltip>
  );

  const _renderBasicDateCell = (value) => (
    <Tooltip title={value || ""} arrow placement="top">
      <div>
        <Text style={{ fontSize: 14 }} key={`key-${value}`}>
          {value || "Sem data"}
        </Text>
      </div>
    </Tooltip>
  );

  const _renderDateCell = (date) => {
    if (!date) return _renderBasicDateCell("Sem data");
    const formattedDate = format(new Date(date), "dd/MM/yyyy 'às'	 HH:mm");
    return _renderBasicDateCell(formattedDate);
  };

  const _renderExpandIcon = (rowData, rowIndex) => {
    const isExpanded = expandedRows.has(rowIndex);
    const hasProductsInRow = hasProducts(rowData);

    if (!hasProductsInRow) return <div style={{ width: 24, height: 24 }} />;

    return (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          toggleRowExpansion(rowIndex);
        }}
        style={{ padding: 4 }}
      >
        {isExpanded ? <ExpandLess /> : <ExpandMore />}
      </IconButton>
    );
  };

  const columnConfig = {
    [columnType.CREATED_AT]: { order: 1, width: 140 },
    // [columnType.PRE_ORDER_NAME]: { order: 2, width: 200 },
    [columnType.PRE_ORDER_NUMBER]: { order: 2, width: 150 },
    [columnType.AUTHOR]: { order: 3, width: 120 },
    [columnType.STATUS]: { order: 4, width: 120 },
    [columnType.EXPAND]: {
      order: 5,
      width: 15,
      noHeader: true,
      noSorting: true,
    },
  };

  const components = {
    [columnType.CREATED_AT]: (value) => _renderDateCell(value),
    // [columnType.PRE_ORDER_NAME]: _renderBasicTextCell,
    [columnType.PRE_ORDER_NUMBER]: (value, list, index) => {
      const id = list[index]?.id;
      return _renderBasicTextCell(id ? `${id}` : "--");
    },
    [columnType.AUTHOR]: _renderBasicTextCell,
    [columnType.STATUS]: (value) =>
      _renderStatusTextCell(translateColumnStatus(value), value),
    [columnType.EXPAND]: (value, list, index) =>
      _renderExpandIcon(list[index], index),
  };

  const toggleConfig = Array.isArray(data)
    ? data.map((_, index) => ({
        isOpen: expandedRows.has(index),
      }))
    : [];

  const rowWrapper = (rowData, rowIndex) => {
    const isExpanded = toggleConfig[rowIndex]?.isOpen || false;
    return {
      RowWrapper: StyledRowWrapper,
      rowWrapperProps: { isExpanded },
    };
  };

  return {
    toggleRowExpansion,
    renderProductsList,
    columnConfig,
    components,
    toggleConfig,
    rowWrapper,
    isAgent,
    isEmployee,
  };
};
